import {
    getPreprocessUpstream,
    getUpstream,
} from 'components/filter/filters-next/filterModuleStoreAccessors';
import { getResourceFromRelationalKey } from 'components/filter/filters-next/relational';
import {
    injectOperator,
    isRelativeDateOperator,
    rawServerKeyFromPrefixedFilter,
} from 'components/filter/filters-next/util';
import {
    ComparisonFilterDefinition,
    ConditionalOperator,
    FilterDefinition,
    FilterGroup,
    FilterGroupRequest,
    FilterOperator,
    FilterRequestValue,
    FilterValue,
    NegationOperator,
    RelationalServerKey,
    RelativeDateRangeOperator,
    ServerKey,
} from 'components/filter/types';
import { OutboundFilterRequest } from 'components/filter/types-legacy';
import { groupBy, omit, partition } from 'lodash';
import { FiltersRequestNext, OutboundFilters, SavedNextFilters } from 'types/request';

type GroupedRelationalFilters = {
    [key in RelationalServerKey]: FilterValue[];
};

const transformFilterIntoNextRequest = (filter: FilterValue): FilterDefinition => {
    const transform = getUpstream(filter.serverKey);

    if (filter.filterOperator && isRelativeDateOperator(filter.filterOperator)) {
        return {
            type: 'date_range',
            date_range: {
                type: filter.filterOperator as RelativeDateRangeOperator,
                range: transform(filter).value as [string, string],
            },
        };
    }

    if (filter.filterOperator === 'bet') {
        return {
            type: 'between',
            values: transform(filter).value as [string, string],
        };
    }

    return {
        type: 'comparison',
        operator: transformNextOperator(filter.filterOperator ?? 'eq'),
        value: transform(filter).value as string,
    };
};

const transformFilterGroup = (group: FilterGroup): OutboundFilters => {
    const [relationalFilters, nonRelationalFilters] = partition(
        group.filters,
        'relationalServerKey',
    );

    const groupedRelationalFilters = relationalFilters.reduce((prev, curr) => {
        if (curr.relationalServerKey) {
            const key = transformUpstreamRelationalKeyByNegationOperator(
                curr.relationalServerKey,
                curr.negationOperator ?? 'has',
            );

            if (prev[key]) {
                prev[key].push(curr);
            } else {
                prev[key] = [curr];
            }
        }

        return prev;
    }, {} as GroupedRelationalFilters);

    return [
        ...(Object.entries(groupedRelationalFilters)
            .filter(([_, value]) => value.length > 0)
            .map(([key, value]) => ({
                children: {
                    [key]: value
                        .map(f =>
                            filterToRequest(omit(f, 'relationalServerKey'), group.innerOperator),
                        )
                        .map(f => ({
                            ...f,
                            type: group.innerOperator,
                        })),
                },
                type: group.innerOperator,
            })) as unknown as OutboundFilters),
        ...nonRelationalFilters
            .map(f => filterToRequest(f, group.innerOperator))
            .map(f => ({
                ...f,
                type: group.innerOperator,
            })),
    ];
};

const transformFilterGroupsToRequest = (groups: FilterGroup[]): OutboundFilters => {
    return groups
        .map(group => ({
            children: transformFilterGroup(group),
            type: group.outerOperator,
        }))
        .filter(requestGroup => requestGroup.children.length > 0);
};

const transformNextFilters = (filters: FilterValue[]): FiltersRequestNext => {
    const groupedFilters = groupBy(filters, filter => filter.serverKey);
    return Object.entries(groupedFilters).reduce((prev, [_key, value]) => {
        const key = rawServerKeyFromPrefixedFilter(_key);

        prev[key] = value
            .map(filter => transformFilterIntoNextRequest(filter))
            .map(filter => ({
                ...filter,
                column: null,
            }));
        return prev;
    }, {} as FiltersRequestNext);
};

const partitionFilters = (filters: FilterGroup[]): SavedNextFilters | OutboundFilters => {
    const isNext = filters.some(f => f.filters.some(f => !f.isLegacy));

    if (isNext) {
        const onlyFilterGroup = filters[0];
        const [legacyFilters, nextFilters] = partition(onlyFilterGroup.filters, f => f.isLegacy);

        return {
            global: transformFilterGroupsToRequest([
                {
                    ...onlyFilterGroup,
                    filters: legacyFilters,
                },
            ]),
            ...transformNextFilters(nextFilters),
        } as SavedNextFilters;
    }

    return transformFilterGroupsToRequest(filters);
};

const filterToRequest = (
    rawFilter: FilterValue,
    innerOperator: ConditionalOperator,
): FilterGroupRequest<OutboundFilterRequest> => {
    const preprocess = getPreprocessUpstream(rawFilter.serverKey);
    const filter = preprocess ? preprocess(rawFilter) : rawFilter;
    // we still need to use the serverKey from the raw filter in case preprocess mutates it
    const transform = getUpstream(rawFilter.serverKey);

    if (filter.relationalServerKey) {
        return filterRequestFactory(
            transformUpstreamRelationalKeyByNegationOperator(
                filter.relationalServerKey,
                filter.negationOperator ?? 'has',
            ),
            filterToRequest(omit(filter, ['relationalServerKey']), innerOperator),
        );
    }

    return filterRequestFactory(filter.serverKey, injectOperator(transform(filter), innerOperator));
};

const transformUpstreamRelationalKeyByNegationOperator = (
    key: RelationalServerKey,
    operator: NegationOperator,
): RelationalServerKey =>
    operator === 'has'
        ? key
        : (`doesnt_have_${getResourceFromRelationalKey(key)}` as RelationalServerKey);

const filterRequestFactory = (
    key: ServerKey | RelationalServerKey,
    child: FilterRequestValue<any> | FilterGroupRequest<any>,
): FilterGroupRequest<any> => {
    return {
        type: 'and',
        children: {
            [key]: [child],
        },
    };
};

const transformNextOperator = (
    operator: FilterOperator,
): ComparisonFilterDefinition['operator'] => {
    switch (operator) {
        case 'lt':
            return '<';
        case 'lte':
            return '<=';
        case 'eq':
            return '=';
        case 'neq':
            return '!=';
        case 'gt':
            return '>';
        case 'gte':
            return '>=';
        default:
            return '=';
    }
};

export {
    filterToRequest,
    transformFilterGroupsToRequest,
    transformFilterIntoNextRequest,
    transformNextFilters,
    partitionFilters,
};
